import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import './Landing.scss';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip } from 'ps-design';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import {
  Button,
  DefaultModalDialog,
  Icon,
  Modal,
  SearchBox,
  Table,
  TableColumn,
  useDisclosure
} from '../../../ps-aspect-design';
import classnames from 'classnames';
import { MatchStrategy, PricingVersion, PropagateMatchOption } from './types/versions';
import { copyVersion, createRule, createVersion, deleteVersion, useVersions } from './GlobalJobMatchingService';
import { formatDateCell, formatDefaultTextCell, formatJobCountCell, formatNameCell } from './LandingCells';
import { PricingRule } from './types/rules';
import { isProduction } from '../../../lib/helpers';
import { useQueryClient } from 'react-query';

async function deleteVersionWithCallback(id: number, masterAccountId: string, cb: Function): Promise<void> {
  const deleteResult = await deleteVersion(masterAccountId, id);
  if (cb) cb(deleteResult);
}

const searchComparisonFunction = (searchString: string, compareString: string): boolean =>
  compareString
    .toString()
    .toLowerCase()
    .includes(searchString.toLowerCase());

const getMasterAccountID = (state: any): string => state.session?.organization?.masterAccountId;

const VERSION_LIMIT = 25;

const GlobalJobMatchingLanding = (): ReactElement => {
  const masterAccountId = useSelector(getMasterAccountID);
  const queryClient = useQueryClient();
  const { key, data } = useVersions(masterAccountId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const history = useHistory();
  const [mainDataBody, setMainDataBody] = useState<PricingVersion[]>(data ? data.data : []);
  const [tableData, setTableData] = useState(mainDataBody);
  const [selectedVersion, setSelectedVersion] = useState<PricingVersion>();

  let atVersionLimit = false;
  if (mainDataBody.length >= VERSION_LIMIT && isProduction()) atVersionLimit = true;

  useEffect(() => {
    setMainDataBody(data ? data.data : []);
  }, [data]);

  useEffect(() => {
    setTableData(mainDataBody);
  }, [mainDataBody]);

  const defaultColumn = useMemo(
    () => ({
      width: 145,
      formatCell: formatDefaultTextCell
    }),
    []
  );

  const handleCopyVersion = useCallback(
    (version: PricingVersion) => async (): Promise<void> => {
      if (!atVersionLimit) {
        const newVersion = await copyVersion(masterAccountId, version.id.toString());
        history.push(`/market-analysis/global-job-matching/${newVersion.data.id}`);
      }
    },
    [history, atVersionLimit]
  );

  const handleDeleteVersion = useCallback(
    (version: PricingVersion) => (): void => {
      setSelectedVersion(version);
      onOpen();
    },
    [setSelectedVersion, onOpen]
  );

  const formatActionCell = useCallback(
    (_: any, version: PricingVersion) => (
      <div className={classnames('global-job-matching__icon-container', { disabled: atVersionLimit })}>
        <span onClick={handleCopyVersion(version)} title="Copy version">
          {atVersionLimit ? (
            <Tooltip
              display="underline"
              tooltipText={<Icon className="icon global-job-matching__copy-icon" name="Docs" />}
              position="left"
              hoverText={`Version limit of ${VERSION_LIMIT} reached.`}
            />
          ) : (
            <Icon className="icon global-job-matching__copy-icon" name="Docs" />
          )}
        </span>
        <span onClick={handleDeleteVersion(version)} title="Delete version">
          <Icon className="icon global-job-matching__trash-icon" name="Trash" />
        </span>
      </div>
    ),
    [handleCopyVersion, handleDeleteVersion, atVersionLimit]
  );

  const memoizedColumns = useMemo<TableColumn[]>(
    () => [
      {
        ...defaultColumn,
        id: 'name',
        name: 'Version Name',
        columnClassName: 'structure-modeling__type-cell',
        formatCell: formatNameCell
      },
      // TODO: Once 'jobCount' is applicable, we can uncomment this
      // {
      //   ...defaultColumn,
      //   id: 'jobCount',
      //   name: 'Job Count',
      //   columnClassName: 'structure-modeling__structure-code-cell',
      //   formatCell: formatJobCountCell
      // },
      {
        ...defaultColumn,
        id: 'createdBy',
        name: 'Created By'
      },
      {
        ...defaultColumn,
        id: 'lastModified',
        name: 'Last Modified',
        formatCell: formatDateCell
      },
      // TODO: Once 'status' is applicable, we can uncomment this
      // {
      //   ...defaultColumn,
      //   id: 'isCurrent',
      //   name: 'Status',
      //   formatCell: formatStatusCell
      // },
      {
        ...defaultColumn,
        id: 'id',
        name: '',
        width: 100,
        isSortable: false,
        formatCell: formatActionCell
      }
    ],
    [formatNameCell, formatJobCountCell, formatDateCell, formatActionCell]
  );

  const handleSearchClear = useCallback(() => setTableData(mainDataBody), [mainDataBody]);
  const handleSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, value?: string | undefined) => {
      if (!event.target.value || event.target.value.length < 4) setTableData(mainDataBody);
      setTableData(
        mainDataBody.filter(
          item =>
            searchComparisonFunction(event.target.value, item['name']) ||
            // searchComparisonFunction(event.target.value, item['status'] ? 'current' : 'draft') ||
            searchComparisonFunction(event.target.value, item['createdBy'])
        )
      );
    },
    [mainDataBody]
  );

  const createNewVersion = useCallback(async () => {
    const newVersion = { name: 'New Version', jobCount: 0, isCurrent: false, matchStrategy: MatchStrategy.CURRENT, propagateMatchOption: PropagateMatchOption.ALL } as PricingVersion;
    const result = await createVersion(masterAccountId, newVersion);
    const newRule = { name: 'Jobs', globalJobPricingVersionId: result.data.id } as PricingRule;
    await createRule(masterAccountId, newRule);
    history.push(`/market-analysis/global-job-matching/${result.data.id}`);
  }, [history, masterAccountId]);

  const deleteSelectedVersion = useCallback(async () => {
    if (selectedVersion) {
      await deleteVersionWithCallback(selectedVersion.id, masterAccountId, onClose);
      queryClient.invalidateQueries(key)
    }
  }, [selectedVersion, masterAccountId, onClose, queryClient]);

  return (
    <div className="global-job-matching aspect-base">
      <Modal isOpen={isOpen} onClose={onClose} className="global-job-matching__delete-dialog">
        <DefaultModalDialog
          primaryText="Delete"
          secondaryText="Cancel"
          primaryCallback={deleteSelectedVersion}
          secondaryCallback={onClose}
          title="Confirm Delete"
          primaryDisabled={false}
          secondaryDisabled={false}
        >
          <span>You have selected to delete version </span>
          <b>“{selectedVersion?.name}”</b>
          <span>. Please confirm your choice, or cancel and return to the page.</span>
        </DefaultModalDialog>
      </Modal>
      <div className="global-job-matching__header">
        <h1 className="global-job-matching__title">Global Job Matching</h1>
        <Button
          className="global-job-matching__create-btn"
          id="global-job-matching__create-btn"
          size="default"
          disabled={atVersionLimit}
          onClick={createNewVersion}
        >
          {atVersionLimit ? (
            <Tooltip
              tooltipText="Create New"
              position="left"
              hoverText={`Version limit of ${VERSION_LIMIT} reached.`}
            />
          ) : (
            'Create New'
          )}
        </Button>
      </div>
      <div className="global-job-matching__search-container">
        <SearchBox
          aria-label="search"
          id="global-job-matching__search"
          inputClassName="global-job-matching__search"
          placeholder="Version name, status, or created by"
          onClear={handleSearchClear}
          onChange={handleSearch}
        />
      </div>
      <div className="global-job-matching__grid-wrapper">
        <Table
          compact={true}
          sortEnabled={true}
          emptyComponent={
            <Placeholder
              className={'global-job-matching__placeholder-container'}
              primaryText={`No versions found.`}
              secondaryText={`Please try updating your search criteria or creating a new version.`}
              showText={true}
            />
          }
          className="global-job-matching__grid"
          columns={memoizedColumns}
          data={tableData}
          fixedHeader
        />
      </div>
    </div>
  );
};

export default GlobalJobMatchingLanding;
